import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrayHelpers, Box, Help, Loading, SlideDown, SystemText } from '@salesperformanceint/e4shared';
import { showWidgetConfig } from 'actions/ui';
import { hasModule } from 'helpers/profile';
import { apiGet } from 'services';
import posthog from 'posthog-js';

export const colourPalette = ['#dd4b39', '#3c8dbc', '#00a65a', '#f39c12', '#00c0ef', '#5416b4'];
export const colourPaletteAutumn = ['#e8c1a0', '#f47560', '#f1e15b', '#e8a838'];

export const tickValues = (data, prop, size, height, extra = {}, format1) => {
    extra.suffix = '';
    extra.format = value => value;

    if (format1 === 'P' && prop === 'y') data = [...data].map(d => ({ ...d, y: d.y * 100 }));

    const spacing = height ? 40 : 100;
    const ticks = Math.round(size / spacing);
    const gapCeiling = gap => {
        const len = gap.toString().length;
        const start = len > 1 ? gap.toString().substr(0, 2) : gap.toString() + '0';
        const startNum = parseInt(start);
        if (startNum <= 10) {
            return 10 * Math.pow(10, len - 2);
        } else if (startNum <= 15) {
            return 15 * Math.pow(10, len - 2);
        } else if (startNum <= 20) {
            return 20 * Math.pow(10, len - 2);
        } else if (startNum <= 25) {
            return 25 * Math.pow(10, len - 2);
        } else if (startNum <= 50) {
            return 50 * Math.pow(10, len - 2);
        } else if (startNum <= 75) {
            return 75 * Math.pow(10, len - 2);
        } else {
            return 100 * Math.pow(10, len - 2);
        }
    };

    if (height) {
        const max = data.reduce((a, c) => (c[prop] > a ? c[prop] : a), 0);
        const min = data.reduce((a, c) => (c[prop] < a ? c[prop] : a), 0);

        if (max === min) return [max];
        if (max > 4000000) {
            extra.suffix = `(mil)`;
            extra.format = value => value / 1000000;
        } else if (max > 4000) {
            extra.suffix = `(000s)`;
            extra.format = value => value / 1000;
        }

        const gap = gapCeiling(Math.round(max / ticks, 2));
        let result = Array(ticks)
            .fill(gap)
            .map((a, i) => a * i);

        if (format1 === 'P' && prop === 'y') result = [...result].map(d => d / 100);

        return result;
    } else {
        const every = Math.round(data.length / ticks);
        const result =
            data.length > 1
                ? data.reduce((a, d, i) => {
                      const result = i % every === 0 ? a.concat([d[prop]]) : a;
                      return result;
                  }, [])
                : [];

        return result;
    }
};

export const goalLegend = (goalId, goals, defaultLegend = 'Goal') => {
    if (!goalId) return defaultLegend;
    const goal = goals.find(c => c.id === goalId);
    return goal ? goal.name : defaultLegend;
};

export const competencyLegend = (competency, competencies, defaultLegend = 'Competency') => {
    if (!competency) return defaultLegend;
    if (competency.areaId === -1) return SystemText('Dashboard/Widget/General/AllCompetencies');
    const comp = competencies.find(c => c.id === competency.id);
    const area = competencies.find(c => c.area_Id === competency.areaId);
    return comp ? `${comp.full_Name}` : area ? `${area.area}` : defaultLegend;
};

export const metricLegend = (metric, metrics, currencySymbol, defaultLegend = 'Metric') => {
    if (!metric) return defaultLegend;
    if (metric.metricRef.indexOf('RT') >= 0) {
        const ref = parseInt(metric.metricRef.replace('RT-', 0), 10);
        const real = metrics.find(m => m.realTime && m.id === ref);
        return real?.name || 'Metric';
    }

    const money = () => (currencySymbol !== null ? ` (${currencySymbol})` : '');

    let metric1 = { ...metric };
    metric1 = metrics.find(
        m => (m.ref || m.integration) === metric.type && (m.id === metric.metricId || m.id === metric.metricRef)
    );

    return metric1
        ? metric1.format === 'M'
            ? `${metric1.name}${money()}`
            : metric1?.format === 'P'
              ? `${metric1.name.replace('(%)', '')} (%)`
              : metric1.name
        : defaultLegend;
};

export const metricFormatFromId = (metric, metrics) => {
    const metric1 = metrics.find(
        m =>
            (m.realTime && metric.metricRef === `RT-${m.id}`) ||
            (metric.metricRef.indexOf('RT-') < 0 &&
                (m.ref || m.integration) === metric.type &&
                (m.id === metric.metricId || m.id === metric.metricRef))
    );

    return metric1 ? metric1.format : 'Q';
};

export const metricNameFromId = (metric, metrics) => {
    const metric1 = metrics.find(
        m =>
            (m.realTime && metric.metricRef === `RT-${m.id}`) ||
            (metric.metricRef.indexOf('RT-') < 0 &&
                (m.ref || m.integration) === metric.type &&
                (m.id === metric.metricId || m.id === metric.metricRef))
    );

    return metric1 ? metric1.name : 'Metric';
};

export const limitNumberWidth = (data, prop, value) => {
    const thous = data.some(d => d[prop] > 1000);
    return thous ? `${(value / 1000).toFixed()}k` : value;
};

export const widgetLoad = (
    id,
    guid,
    validator,
    setLoading,
    setNotConfigured,
    setData,
    onSuccess,
    validDataOverride,
    qsParams = ''
) => {
    setLoading(true);
    if (validator(true)) {
        apiGet(`widget-data/${id}${qsParams}`, guid, response => {
            if (response.error === 'INVALID_DATA') {
                setNotConfigured(true);
                setLoading(false);
            } else {
                if (validDataOverride) {
                    validDataOverride(response);
                    onSuccess && onSuccess();
                } else {
                    setData(response.data);
                    onSuccess && onSuccess();
                }

                setNotConfigured(false);
                setLoading(false);
            }
        });
    } else {
        setNotConfigured(true);
        setLoading(false);
    }
};

const lastWidgetRec = {};
export const widgetLoadRecommendations = (id, guid, setRecLoading, setRecommendationData, qsParams = '') => {
    const now = Date.now();
    lastWidgetRec[id] = now;
    apiGet(`widget-recommendation/${id}${qsParams}`, guid, response => {
        if (lastWidgetRec[id] === now) {
            if (response.error === 'INVALID_DATA') {
                // Do nothing
            } else {
                setRecommendationData(response.recommendation);
            }

            setRecLoading(false);
        }
    });
};

export const groupSet = group => {
    if (!group) return false;
    return (
        group.profileIds?.length ||
        group.roleIds?.length ||
        group.teamIds?.length ||
        group.reportIds?.length ||
        group.departmentIds?.length ||
        group.locationIds?.length ||
        group.unitIds?.length
    );
};

export const groupTooLong = group => {
    if (!group) return false;
    return (
        group.profileIds?.length > 10 ||
        group.roleIds?.length > 10 ||
        group.teamIds?.length > 10 ||
        group.departmentIds?.length > 10 ||
        group.locationIds?.length > 10 ||
        group.unitIds?.length > 10
    );
};

export const metricSet = metric => {
    if (!metric) return false;
    return metric.metricId;
};

export const competencySet = competency => {
    if (!competency) return false;
    return competency.areaId;
};

export const WidgetBase = ({
    filters,
    chart,
    navigation,
    height,
    item,
    id,
    help,
    hideConfig,
    extra,
    loading,
    notConfigured,
    recommendations,
}) => {
    const dispatch = useDispatch();

    const [recIndex, setRecIndex] = React.useState(0);
    const widget = React.useRef(null);
    const sliderLogo = React.useRef(null);
    const user = useSelector(state => state.user.user);

    const recommendsLive =
        !notConfigured &&
        hasModule(user, 'integrations') &&
        user.companySettings?.development.showRecommends &&
        recommendations;

    const st = path => SystemText(`Dashboard/Widgets/General/${path}`);

    const recommendLogo = title => {
        return recommendsLive ? (
            <div
                ref={sliderLogo}
                data-testid="logo-show-recommendations"
                className="pointer e4-logo mr10 highlight"
                title={title}
                onMouseOver={() => {
                    posthog.capture('insights-target-icon-hover');
                }}
            />
        ) : null;
    };

    const recommendationsArea = recs => {
        if (!ArrayHelpers.existsAndHasLength(recs)) return recs;
        return (
            <div>
                <div className="fade-container">
                    {recs.map((r, i) => {
                        return (
                            <span
                                key={`recommendation-${id}-${i}`}
                                // style={{ display: i === recIndex ? '' : 'none' }}
                                className={i === recIndex ? 'fadein' : 'fadeout'}
                            >
                                {r}
                            </span>
                        );
                    })}
                </div>
                {recs.length > 1 && (
                    <div className="">
                        <button
                            className="btn btn-primary btn-sm mt10"
                            onClick={() => setRecIndex(recIndex + 1 === recs.length ? 0 : recIndex + 1)}
                        >
                            {SystemText('Dashboard/Widgets/General/Recommends/Next')} ( {recIndex + 1} / {recs.length} )
                            <i className="fa fa-caret-right text-sm ml10" />
                        </button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div data-testid="widget-base" className="box" ref={widget} data-widgetid={id} data-widget-type={item.widget}>
            {recommendsLive && (
                <SlideDown header={item.name} content={recommendationsArea(recommendations)} sliderLogo={sliderLogo} />
            )}
            <div className="box-header with-border flex flex-center">
                {recommendLogo(st('Recommends/Show'))}
                <div className="flex-grow ellipsis" style={{ cursor: 'grab' }}>
                    {item.name}
                    {help && <Help id={`widget-help-${id}`}>{help}</Help>}
                </div>
                {item.w > 1 && extra && extra()}
                {!hideConfig && (
                    <i
                        className="fa fa-sm fa-cog pointer"
                        data-testid="button-widget-cog"
                        onClick={() => dispatch(showWidgetConfig(id))}
                    />
                )}
            </div>
            {navigation}
            <div className="box-body">
                <div style={{ height: `${height}px` }}>
                    {loading ? <Loading /> : notConfigured ? <div className="text-center">{st('Configure')}</div> : chart}
                    {filters}
                </div>
            </div>
        </div>
    );
};

export default WidgetBase;
